import React, { useState, useEffect } from 'react';
import { View, StyleSheet, Text, TouchableOpacity, FlatList, Dimensions, Image } from 'react-native';
import { useRoute, RouteProp, useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '../types/navigation';
import { VideoPost } from '../types/video';
import VideoPlayer from '../components/video/VideoPlayer';
import CommentsModal from '../components/video/CommentsModal';
import { likePost, unlikePost, repost, unrepost, getPostThread } from '../services/bluesky';
import { formatViews, formatRelativeTime } from '../utils/video';

type ShortsPlayerRouteProp = RouteProp<RootStackParamList, 'ShortsPlayer'>;
type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

interface Comment {
  id: string;
  authorName: string;
  authorHandle: string;
  authorAvatar?: string;
  authorDid: string;
  text: string;
  createdAt: string;
  likeCount: number;
  uri: string;
  cid: string;
  likeUri?: string;
  facets?: any[];
  embed?: any;
}

const { height: SCREEN_HEIGHT } = Dimensions.get('window');

const ShortsPlayerScreen: React.FC = () => {
  const route = useRoute<ShortsPlayerRouteProp>();
  const navigation = useNavigation<NavigationProp>();
  const { videos, initialIndex } = route.params;
  const [currentIndex, setCurrentIndex] = useState(initialIndex || 0);
  const [commentsModalVisible, setCommentsModalVisible] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [likeLoading, setLikeLoading] = useState(false);
  const [repostLoading, setRepostLoading] = useState(false);
  
  const currentVideo = videos[currentIndex];

  useEffect(() => {
    if (currentVideo) {
      fetchComments(currentVideo.originalUri || currentVideo.uri);
    }
  }, [currentIndex]);

  const fetchComments = async (uri: string) => {
    try {
      const thread = await getPostThread(uri);
      if (thread && thread.replies) {
        const mappedComments = thread.replies
          .filter((reply: any) => reply.post)
          .map((reply: any) => ({
            id: reply.post.cid,
            authorName: reply.post.author.displayName || reply.post.author.handle,
            authorHandle: reply.post.author.handle,
            authorAvatar: reply.post.author.avatar,
            authorDid: reply.post.author.did,
            text: reply.post.record.text,
            createdAt: formatRelativeTime(reply.post.record.createdAt),
            likeCount: reply.post.likeCount || 0,
            uri: reply.post.uri,
            cid: reply.post.cid,
            likeUri: reply.post.viewer?.like,
            facets: reply.post.record.facets,
            embed: reply.post.embed,
          }));
        setComments(mappedComments);
      }
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  };

  const handleLikeToggle = async () => {
    if (!currentVideo || likeLoading) return;

    try {
      setLikeLoading(true);
      const isLiked = !!currentVideo.likeUri;
      
      if (isLiked && currentVideo.likeUri) {
        await unlikePost(currentVideo.likeUri);
        videos[currentIndex] = {
          ...currentVideo,
          likeUri: undefined,
          likeCount: Math.max(0, currentVideo.likeCount - 1),
        };
      } else {
        const uri = await likePost(currentVideo.originalUri || currentVideo.uri, currentVideo.originalCid || currentVideo.cid);
        videos[currentIndex] = {
          ...currentVideo,
          likeUri: uri,
          likeCount: currentVideo.likeCount + 1,
        };
      }
      setCurrentIndex(currentIndex); // 再レンダリングをトリガー
    } catch (error) {
      console.error('Failed to toggle like:', error);
    } finally {
      setLikeLoading(false);
    }
  };

  const handleRepostToggle = async () => {
    if (!currentVideo || repostLoading) return;

    try {
      setRepostLoading(true);
      const isReposted = !!currentVideo.repostUri;
      
      if (isReposted && currentVideo.repostUri) {
        await unrepost(currentVideo.repostUri);
        videos[currentIndex] = {
          ...currentVideo,
          repostUri: undefined,
          repostCount: Math.max(0, currentVideo.repostCount - 1),
        };
      } else {
        const uri = await repost(currentVideo.originalUri || currentVideo.uri, currentVideo.originalCid || currentVideo.cid);
        videos[currentIndex] = {
          ...currentVideo,
          repostUri: uri,
          repostCount: currentVideo.repostCount + 1,
        };
      }
      setCurrentIndex(currentIndex);
    } catch (error) {
      console.error('Failed to toggle repost:', error);
    } finally {
      setRepostLoading(false);
    }
  };

  const handleCommentLikeToggle = async (comment: Comment) => {
    try {
      if (comment.likeUri) {
        await unlikePost(comment.likeUri);
        setComments(prev => prev.map(c => 
          c.id === comment.id 
            ? { ...c, likeUri: undefined, likeCount: Math.max(0, c.likeCount - 1) } 
            : c
        ));
      } else {
        const uri = await likePost(comment.uri, comment.cid);
        setComments(prev => prev.map(c => 
          c.id === comment.id 
            ? { ...c, likeUri: uri, likeCount: c.likeCount + 1 } 
            : c
        ));
      }
    } catch (error) {
      console.error('Failed to toggle comment like:', error);
    }
  };

  const handleCommentReply = (comment: Comment) => {
    setCommentsModalVisible(false);
    navigation.navigate('Compose', {
      replyTo: {
        uri: comment.uri,
        cid: comment.cid,
        rootUri: currentVideo?.originalUri || currentVideo?.uri,
        rootCid: currentVideo?.originalCid || currentVideo?.cid,
        authorHandle: comment.authorHandle,
      },
    });
  };

  const renderVideoItem = ({ item, index }: { item: VideoPost; index: number }) => {
    const isActive = index === currentIndex;
    
    return (
      <View style={styles.videoContainer}>
        {isActive && (
          <VideoPlayer 
            videoUrl={item.videoUrl}
            thumbnailUrl={item.thumbnailUrl}
            type={item.type}
          />
        )}
        
        {/* オーバーレイ情報 */}
        <View style={styles.overlay}>
          {/* 左側: 動画情報 */}
          <View style={styles.leftInfo}>
            <TouchableOpacity 
              style={styles.channelInfo}
              onPress={() => navigation.navigate('ChannelDetail', { 
                did: item.authorDid, 
                handle: item.channelHandle 
              })}
            >
              {item.channelAvatar ? (
                <Image source={{ uri: item.channelAvatar }} style={styles.avatar} />
              ) : (
                <View style={[styles.avatar, { backgroundColor: '#333' }]} />
              )}
            </TouchableOpacity>
            
            <View style={styles.videoInfo}>
              <Text style={styles.channelName}>@{item.channelHandle}</Text>
              <Text style={styles.title} numberOfLines={2}>{item.title}</Text>
            </View>
          </View>

          {/* 右側: アクションボタン */}
          <View style={styles.rightActions}>
            <TouchableOpacity 
              style={styles.actionButton}
              onPress={handleLikeToggle}
              disabled={likeLoading}
            >
              <Ionicons 
                name={item.likeUri ? "heart" : "heart-outline"} 
                size={32} 
                color={item.likeUri ? "#FF0000" : "#fff"} 
              />
              <Text style={styles.actionText}>{formatViews(item.likeCount)}</Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.actionButton}
              onPress={handleRepostToggle}
              disabled={repostLoading}
            >
              <Ionicons 
                name={item.repostUri ? "repeat" : "repeat-outline"} 
                size={32} 
                color={item.repostUri ? "#00BA7C" : "#fff"} 
              />
              <Text style={styles.actionText}>
                {item.repostCount > 0 ? formatViews(item.repostCount) : ''}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.actionButton}
              onPress={() => setCommentsModalVisible(true)}
            >
              <Ionicons name="chatbubble-outline" size={32} color="#fff" />
              <Text style={styles.actionText}>
                {item.commentCount ? formatViews(item.commentCount) : ''}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.actionButton}
              onPress={() => navigation.navigate('VideoPlayback', { 
                videoId: item.id, 
                video: item 
              })}
            >
              <Ionicons name="ellipsis-horizontal" size={32} color="#fff" />
            </TouchableOpacity>
          </View>
        </View>

        {/* 閉じるボタン */}
        <TouchableOpacity 
          style={styles.closeButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="close" size={28} color="#fff" />
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <FlatList
        data={videos}
        renderItem={renderVideoItem}
        keyExtractor={(item) => item.id}
        pagingEnabled
        showsVerticalScrollIndicator={false}
        snapToInterval={SCREEN_HEIGHT}
        snapToAlignment="start"
        decelerationRate="fast"
        onMomentumScrollEnd={(event) => {
          const index = Math.round(event.nativeEvent.contentOffset.y / SCREEN_HEIGHT);
          setCurrentIndex(index);
        }}
        initialScrollIndex={initialIndex}
        getItemLayout={(_, index) => ({
          length: SCREEN_HEIGHT,
          offset: SCREEN_HEIGHT * index,
          index,
        })}
      />

      {/* コメントモーダル */}
      <CommentsModal
        visible={commentsModalVisible}
        onClose={() => setCommentsModalVisible(false)}
        comments={comments}
        onLikeComment={handleCommentLikeToggle}
        onReplyComment={handleCommentReply}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
  },
  videoContainer: {
    height: SCREEN_HEIGHT,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#000',
  },
  overlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 16,
    paddingBottom: 80,
  },
  leftInfo: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  channelInfo: {
    marginBottom: 12,
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    borderWidth: 2,
    borderColor: '#fff',
  },
  videoInfo: {
    marginTop: 8,
  },
  channelName: {
    color: '#fff',
    fontSize: 15,
    fontWeight: 'bold',
    marginBottom: 4,
    textShadowColor: 'rgba(0, 0, 0, 0.75)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 3,
  },
  title: {
    color: '#fff',
    fontSize: 14,
    lineHeight: 20,
    textShadowColor: 'rgba(0, 0, 0, 0.75)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 3,
  },
  rightActions: {
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  actionButton: {
    alignItems: 'center',
    marginBottom: 20,
  },
  actionText: {
    color: '#fff',
    fontSize: 12,
    marginTop: 4,
    fontWeight: '600',
    textShadowColor: 'rgba(0, 0, 0, 0.75)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 3,
  },
  closeButton: {
    position: 'absolute',
    top: 50,
    right: 16,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default ShortsPlayerScreen;
